import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { SessionIdleService } from '../services/session-idle.service';

/**
 * Authentication interceptor (cookie-session model)
 *
 * The Parent Portal authenticates to the GTLC backend with the
 * `gtlc_parent_session` httpOnly cookie, which the browser attaches
 * automatically to same-site requests because `api.service.ts` sends
 * `withCredentials: true`. No Authorization header is ever set manually —
 * see design.md Decision 1 and task 4.1.
 *
 * 4.7 (R29, DCMPDEV-738 / DCM-366): the request still passes through
 * unchanged; the RESPONSE side now does two things:
 *
 * 1. Any successful API response resets the session-idle clock
 *    (`SessionIdleService.touch()` — the 25-minute idle warning).
 * 2. A 401 while the client still believes it is signed in sends the parent
 *    to /login with `{ expired: 1, returnUrl }`, so a session that ended
 *    after 30 idle minutes never leaves a signed-in-looking UI where every
 *    save silently fails. 403 and every other status are left untouched.
 *
 * Guards against a navigation loop: never navigate away from /login itself
 * (a wrong password answers 401 there), and never while the client is not
 * signed in (the login/MFA/account-setup 401s are legitimate auth failures
 * those screens already surface inline).
 *
 * AuthService is resolved LAZILY (injector.get in the error callback, not
 * inject() in the body): the boot whoami request fires from AuthService's
 * own constructor while it is still being instantiated, so an eager inject()
 * here would be an instantiation cycle at app start. A 401 response always
 * arrives later, on the XHR callback, when AuthService exists.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const injector = inject(Injector);
  const sessionIdle = inject(SessionIdleService);

  return next(req).pipe(
    tap({
      next: (event: HttpEvent<unknown>): void => {
        if (event instanceof HttpResponse && event.ok) {
          sessionIdle.touch();
        }
      },
      error: (err: unknown): void => {
        if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
          return;
        }
        const auth = injector.get(AuthService);
        // Not signed in: a guest/auth screen's own 401 (wrong password or
        // MFA code, invalid setup token) — those screens handle it inline.
        if (!auth.isAuthenticated) {
          return;
        }
        // Already on the login page: never navigate away from it (loop).
        if (router.url.startsWith('/login')) {
          return;
        }
        // Clear the STALE client-side authenticated state first — otherwise
        // guestGuard sees isAuthenticated === true and bounces the parent
        // straight back into the dead session.
        auth.markSessionExpired();
        void router.navigate(['/login'], {
          queryParams: { expired: 1, returnUrl: router.url },
        });
      },
    }),
  );
};